import React, { useState } from "react";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Toast,
  ToastContainer,
  Modal,
  Dropdown,
} from "react-bootstrap";
import ConfirmationDialog from "../common/dialog/ConfirmationDialog";
import { ReactComponent as Settings } from "../../resources/gear-fill.svg";
import { ReactComponent as Home } from "../../resources/house-door-fill.svg";
import { getUserState, getNotifications } from "../../app/selector";
import { userLogin, userLogout } from "../../features/user/userSlice";
import { resetPhotosGallery } from "../../features/gallery/gallerySlice";
import { resetProfile } from "../../features/profile/profileSlice";
import { resetPhotos } from "../../features/photo/photoSlice";
import { resetRandomPhotos } from "../../features/individualRating/individualRatingSlice";
import axios from "axios";
import { BACKEND_URL } from "constants";
import { PersonCircle, ChevronDown } from "react-bootstrap-icons";
import LoginForm from "./LoginForm";
import MobileLoginModal from "./MobileLoginModal";
import MobileRegistrationForm from "./MobileRegistrationForm";
import UploadButton from "../common/button/UploadButton";

const logo = require("../../resources/Logo.jpeg");

let handleLogout = null;

const Header = () => {
  const { loggedIn, user } = useSelector(getUserState);
  const { messages, confirms, requests } = useSelector(getNotifications);

  const [show, setShow] = useState(false);
  const [loginDisabled, setLoginDisabled] = useState(false);
  const [showMobileLogin, setShowMobileLogin] = useState(false);
  const [showMobileRegister, setShowMobileRegister] = useState(false);

  const [showMobileLoggedMenu, setShowMobileLoggedMenu] = useState(false);
  const [showNewsSubmenu, setShowNewsSubmenu] = useState(false);
  const [showSearchUserSubmenu, setShowSearchUserSubmenu] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "success",
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const sanitizeInput = (str) => str.replace(/[<>/'"%;()&+\\=]/g, "");

  const closeMobileMenu = () => {
    setShowMobileLoggedMenu(false);
    setShowNewsSubmenu(false);
    setShowSearchUserSubmenu(false);
  };

  handleLogout = () => {
    // 1. REMOVE TOKEN FROM COOKIES AND AXIOS
    Cookies.remove("bambkuss-token");
    delete axios.defaults.headers.common["Authorization"];
    dispatch(userLogout());
    dispatch(resetProfile());
    dispatch(resetPhotos());
    dispatch(resetPhotosGallery());
    dispatch(resetRandomPhotos());
    closeMobileMenu();
    navigate("/");
  };

  const goHome = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    closeMobileMenu();
    navigate("/");
  };

  const goSetting = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    closeMobileMenu();
    navigate("/settings");
  };

  const goToPage = (page) => {
    closeMobileMenu();
    navigate(page);
  };

  const openLogoutConfirmation = () => {
    closeMobileMenu();
    setShow(true);
  };

  const toggleNewsSubmenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowNewsSubmenu((prev) => !prev);
  };

  const toggleSearchUserSubmenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSearchUserSubmenu((prev) => !prev);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginDisabled(true);

    const formData = new FormData(e.target);
    const username = sanitizeInput(formData.get("username") || "");
    const password = sanitizeInput(formData.get("password") || "");

    if (
      !username ||
      !password ||
      username.length > 100 ||
      password.length > 100
    ) {
      setToast({
        show: true,
        message: "Invalid input.",
        variant: "danger",
      });
      setLoginDisabled(false);
      return;
    }

    try {
      const { data } = await axios.post(`${BACKEND_URL}/login`, {
        username,
        password,
      });

      // 1. SAVE TOKEN TO COOKIES (so it persists on refresh)
      Cookies.set("bambkuss-token", data.token, { expires: 1 });

      // 2. SET AXIOS DEFAULT HEADER (so immediate subsequent requests use it)
      axios.defaults.headers.common["Authorization"] = "Bearer " + data.token;

      dispatch(userLogin({ user: data }));

      setToast({
        show: true,
        message: "Login successful!",
        variant: "success",
      });

      setShowMobileLogin(false);
      navigate("/");
    } catch (error) {
      console.error("Login error:", error);

      let errorMessage = "Login failed. Please try again.";

      if (error.response) {
        if (error.response.status === 429) {
          errorMessage =
            "Too many login attempts. Please wait a minute and try again.";
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message && error.message.includes("CORS")) {
          errorMessage =
            "Too many requests or network issue detected. Please wait before trying again.";
        }
      }

      setToast({
        show: true,
        message: errorMessage,
        variant: "danger",
      });
    } finally {
      setTimeout(() => setLoginDisabled(false), 3000);
    }
  };

  const handleForgetPassword = () => {
    setShowMobileLogin(false);
    navigate("/forget-password");
  };

  const usernameDisplay = user?.username
    ? user.username.charAt(0).toUpperCase() + user.username.slice(1)
    : "User";

  const totalNewsNotifications = messages + requests + confirms;

  return (
    <>
      <ToastContainer className="p-3" position="top-center">
        <Toast
          onClose={() => setToast({ ...toast, show: false })}
          show={toast.show}
          delay={3000}
          autohide
          bg={toast.variant}
        >
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <header className="header">
        <div className="header-container">
          <div className="logo" onClick={goHome}>
            <img src={logo} alt="Logo" />
          </div>

          <ConfirmationDialog
            text="Do you want to logout?"
            variant="danger"
            operation="Logout"
            show={show}
            setShow={setShow}
            onConfirm={handleLogout}
          />

          {loggedIn ? (
            <>
              <div className="logout-button desktop-logout">
                <Button
                  variant="outline-success"
                  className="round-button-s margin-right"
                  onClick={goHome}
                >
                  <Home width="24" height="24" />
                </Button>

                <Button
                  variant="outline-success"
                  className="round-button-s margin-right"
                  onClick={goSetting}
                >
                  <Settings width="24" height="24" />
                </Button>

                <Button
                  variant="success"
                  onClick={() => setShow(true)}
                  className="logout raised-button"
                >
                  Logout
                </Button>
              </div>

              <div className="mobile-auth-menu-wrapper logged-in-menu">
                <Dropdown
                  align="end"
                  show={showMobileLoggedMenu}
                  onToggle={(nextShow) => {
                    setShowMobileLoggedMenu(nextShow);

                    if (!nextShow) {
                      setShowNewsSubmenu(false);
                      setShowSearchUserSubmenu(false);
                    }
                  }}
                  autoClose="outside"
                >
                  <Dropdown.Toggle
                    variant="light"
                    id="mobile-logged-in-toggle"
                    className="mobile-auth-toggle"
                  >
                    <span className="mobile-auth-toggle-inner">
                      <span className="mobile-auth-toggle-icon">
                        <PersonCircle size={18} />
                      </span>

                      <span className="mobile-auth-toggle-text">
                        {usernameDisplay}

                        {totalNewsNotifications > 0 && (
                          <span className="mobile-user-news-badge">
                            {totalNewsNotifications}
                          </span>
                        )}
                      </span>

                      <ChevronDown
                        size={14}
                        className="mobile-auth-toggle-arrow"
                      />
                    </span>
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="mobile-auth-dropdown">
                    <Dropdown.Item as="div" className="mobile-auth-upload-item">
                      <UploadButton />
                    </Dropdown.Item>

                    <Dropdown.Divider />

                    <Dropdown.Item onClick={() => goToPage("/profile")}>
                      <span className="mobile-auth-item">My Profile</span>
                    </Dropdown.Item>

                    <div className="mobile-auth-parent-row">
                      <Dropdown.Item
                        className="mobile-auth-parent-link"
                        onClick={() => goToPage("/news")}
                      >
                        <span className="mobile-news-label">
                          <span className="mobile-auth-item">News</span>

                          {totalNewsNotifications > 0 && (
                            <span className="mobile-news-badge">
                              {totalNewsNotifications}
                            </span>
                          )}
                        </span>
                      </Dropdown.Item>

                      <button
                        type="button"
                        className="mobile-auth-submenu-icon-button"
                        onClick={toggleNewsSubmenu}
                        aria-label="Toggle News submenu"
                      >
                        <ChevronDown
                          size={14}
                          className={`mobile-auth-submenu-arrow ${showNewsSubmenu ? "open" : ""
                            }`}
                        />
                      </button>
                    </div>

                    {showNewsSubmenu && (
                      <div className="mobile-auth-submenu">
                        <Dropdown.Item
                          className="mobile-auth-submenu-item"
                          onClick={() => goToPage("/inbox")}
                        >
                          <span className="mobile-news-label">
                            <span className="mobile-auth-item">Chat</span>

                            {messages > 0 && (
                              <span className="mobile-news-badge">
                                {messages}
                              </span>
                            )}
                          </span>
                        </Dropdown.Item>

                        <Dropdown.Item
                          className="mobile-auth-submenu-item"
                          onClick={() => goToPage("/requests")}
                        >
                          <span className="mobile-news-label">
                            <span className="mobile-auth-item">Requests</span>

                            {requests > 0 && (
                              <span className="mobile-news-badge">
                                {requests}
                              </span>
                            )}
                          </span>
                        </Dropdown.Item>

                        <Dropdown.Item
                          className="mobile-auth-submenu-item"
                          onClick={() => goToPage("/accepted-requests")}
                        >
                          <span className="mobile-news-label">
                            <span className="mobile-auth-item">Confirms</span>

                            {confirms > 0 && (
                              <span className="mobile-news-badge">
                                {confirms}
                              </span>
                            )}
                          </span>
                        </Dropdown.Item>
                      </div>
                    )}

                    <div className="mobile-auth-parent-row">
                      <Dropdown.Item
                        className="mobile-auth-parent-link"
                        onClick={() => goToPage("/search-user")}
                      >
                        <span className="mobile-auth-item">Search User</span>
                      </Dropdown.Item>

                      <button
                        type="button"
                        className="mobile-auth-submenu-icon-button"
                        onClick={toggleSearchUserSubmenu}
                        aria-label="Toggle Search User submenu"
                      >
                        <ChevronDown
                          size={14}
                          className={`mobile-auth-submenu-arrow ${showSearchUserSubmenu ? "open" : ""
                            }`}
                        />
                      </button>
                    </div>

                    {showSearchUserSubmenu && (
                      <div className="mobile-auth-submenu">
                        <Dropdown.Item
                          className="mobile-auth-submenu-item"
                          onClick={() => goToPage("/gallery")}
                        >
                          <span className="mobile-auth-item">Gallery</span>
                        </Dropdown.Item>

                        <Dropdown.Item
                          className="mobile-auth-submenu-item"
                          onClick={() => goToPage("/individual-rating")}
                        >
                          <span className="mobile-auth-item">
                            Individual Rating
                          </span>
                        </Dropdown.Item>

                        <Dropdown.Item
                          className="mobile-auth-submenu-item"
                          onClick={() => goToPage("/find-username")}
                        >
                          <span className="mobile-auth-item">
                            Find Username
                          </span>
                        </Dropdown.Item>

                        <Dropdown.Item
                          className="mobile-auth-submenu-item"
                          onClick={() => goToPage("/find-user-location")}
                        >
                          <span className="mobile-auth-item">
                            Find User Location
                          </span>
                        </Dropdown.Item>
                      </div>
                    )}

                    <Dropdown.Divider />

                    <Dropdown.Item onClick={goHome}>
                      <span className="mobile-auth-item">Home</span>
                    </Dropdown.Item>

                    <Dropdown.Item onClick={goSetting}>
                      <span className="mobile-auth-item">Settings</span>
                    </Dropdown.Item>

                    <Dropdown.Divider />

                    <Dropdown.Item onClick={openLogoutConfirmation}>
                      <span className="mobile-auth-item text-danger">
                        Logout
                      </span>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </>
          ) : (
            <>
              <div className="login-form desktop-login">
                <LoginForm
                  onSubmit={handleLogin}
                  onForgotPassword={handleForgetPassword}
                  disabled={loginDisabled}
                  isForgotPasswordPage={location.pathname === "/forget-password"}
                  className="login-form-fields"
                  buttonClassName="login-button raised-button d-flex align-items-center justify-content-center"
                />
              </div>

              <div className="mobile-auth-menu-wrapper">
                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="light"
                    id="mobile-auth-toggle"
                    className="mobile-auth-toggle"
                  >
                    <span className="mobile-auth-toggle-inner">
                      <span className="mobile-auth-toggle-icon">
                        <PersonCircle size={18} />
                      </span>

                      <span className="mobile-auth-toggle-text">Account</span>

                      <ChevronDown
                        size={14}
                        className="mobile-auth-toggle-arrow"
                      />
                    </span>
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="mobile-auth-dropdown">
                    <Dropdown.Item onClick={() => setShowMobileLogin(true)}>
                      <span className="mobile-auth-item">Login</span>
                    </Dropdown.Item>

                    <Dropdown.Item onClick={() => setShowMobileRegister(true)}>
                      <span className="mobile-auth-item">Register</span>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <MobileLoginModal
                show={showMobileLogin}
                onHide={() => setShowMobileLogin(false)}
                onSubmit={handleLogin}
                onForgotPassword={handleForgetPassword}
                disabled={loginDisabled}
                isForgotPasswordPage={location.pathname === "/forget-password"}
              />

              <Modal
                show={showMobileRegister}
                onHide={() => setShowMobileRegister(false)}
                centered
                scrollable
              >
                <Modal.Header closeButton>
                  <Modal.Title>Register</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                  <MobileRegistrationForm />
                </Modal.Body>
              </Modal>
            </>
          )}
        </div>

        <div className="line-1"></div>
      </header>
    </>
  );
};

export { Header, handleLogout };